import axios from 'axios'

import { USER_SERVICE_BASE_URL } from '../config'
import { getUserId } from '../functions/authHelpers'
import { fetchUser } from '../users/users'

import { getUtmParametersFromCookie } from './../../function/cookie'

type AuthReturnType = Promise<{
  status: string
  data: {
    authToken: string
    refreshToken: string
  } | null
  error: string | undefined
  statusCode?: number
}>

type ResponseReturnType = Promise<{
  status: string
  data: string | null
  error: string | undefined
  statusCode?: number
}>

// return jwt as data when status is successful
export const login = async ({
  username,
  password,
}: {
  username: string
  password: string
}): AuthReturnType => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/login`,
      {
        email: username,
        password,
      },
      { withCredentials: true }
    )

    const result = response.data

    if (response.status === 200 || response.status === 201) {
      return {
        status: 'ok',
        data: {
          authToken: result.token,
          refreshToken: result.refresh_token,
        },
        error: undefined,
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response?.data?.message || 'Something went wrong',
    }
  }
}

// return jwt as data when status is successful
// * * when using signup on business portal `role` param is required
export const googleSignin = async ({
  token,
  role,
  pcSource,
  pcMedium,
}: {
  token: string
  role?: UserRole
  pcSource?: string | null
  pcMedium?: string | null
}): AuthReturnType => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/google/login`,
      {
        token,
        role,
        pc_source: pcSource,
        pc_medium: pcMedium,
      },
      { withCredentials: true }
    )
    const result = await response.data

    if (response.status === 200 || response.status === 201) {
      return {
        status: 'ok',
        data: {
          authToken: result.access_token,
          refreshToken: result.refresh_token,
        },
        error: undefined,
      }
    } else if (response.status === 409) {
      // User already exists
      return {
        status: 'error',
        data: null,
        error: undefined,
        statusCode: response.status,
      }
    }
    throw new Error(result.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response?.data?.message || 'Something went wrong',
    }
  }
}

// return jwt as data when status is successful
// * * when using signup on business portal `role` param is required
export const googleSignup = async ({
  token,
  role,
  pcSource,
  pcMedium,
}: {
  token: string
  role?: UserRole
  pcSource?: string | null
  pcMedium?: string | null
}): AuthReturnType => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/registrations/google`,
      {
        token,
        role,
        pc_source: pcSource,
        pc_medium: pcMedium,
      },
      { withCredentials: true }
    )
    const result = response.data

    if (response.status === 201) {
      return {
        status: 'ok',
        data: {
          authToken: result.token,
          refreshToken: result.refresh_token,
        },
        error: undefined,
      }
    } else if (response.status === 409) {
      // User already exists
      return {
        status: 'error',
        data: null,
        error: 'User already exists',
        statusCode: response.status,
      }
    }
    throw new Error(result.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response?.data?.message || 'Something went wrong',
    }
  }
}

// return `otp_id` as data when status is successful
export const requestOTP = async ({
  email,
}: {
  email: string
}): ResponseReturnType => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/send-reset-password-otp`,
      {
        email,
      }
    )

    if (response.status === 201) {
      return {
        status: 'ok',
        data: response.data.id, // OTP id
        error: undefined,
      }
    }

    throw new Error(response.data.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response.data.message || 'Something went wrong',
    }
  }
}

// return OTP id as data when status is successful
export const requestSignupOTP = async ({
  email,
  first_name,
  role,
}: {
  email: string
  first_name: string
  role?: UserRole
}): ResponseReturnType => {
  try {
    const utmParams = getUtmParametersFromCookie()

    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/registrations/email-verification-otp`,
      {
        email,
        first_name,
        ...(role && { role }),
        ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
        ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
        ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
      }
    )

    if (response.status === 201) {
      return {
        status: 'ok',
        data: response.data.id,
        error: undefined,
      }
    }
    throw new Error(response.data.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.response.data.message,
    }
  }
}

type SignupParams = {
  email: string
  otp_id?: string
  otp?: string
  first_name: string
  last_name: string
  invite_code?: string
  phone?: string
  password: string
  role: UserRole
  user_invite_code?: string
  invite_hash?: string
  pcSource?: string | null
  pcMedium?: string | null
}

// return jwt as data when status is successful
export const confirmSignup = async ({
  email,
  otp_id,
  otp,
  first_name,
  last_name,
  phone,
  password,
  invite_code = undefined,
  role,
  user_invite_code,
  invite_hash,
  pcSource,
  pcMedium,
}: SignupParams): Promise<{
  status: string
  data: {
    description: string
    id: string
    status: string
    token: string
    refresh_token: string
  } | null
  error: string | undefined
}> => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/registrations`,
      {
        email,
        first_name,
        ...(last_name.length > 0 && {
          last_name,
        }),
        phone,
        role,
        password,
        ...(otp_id && { otp_id }),
        ...(otp && { otp }),
        ...(invite_code && { invite_code }),
        ...(user_invite_code && { user_invite_code }),
        ...(invite_hash && { invite_hash }),
        pc_source: pcSource,
        pc_medium: pcMedium,
      },
      { withCredentials: true }
    )

    const result = response.data
    if (response.status === 201) {
      return {
        status: 'ok',
        data: result,
        error: undefined,
      }
    }
    throw new Error(result.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response.data.message || 'Something went wrong',
    }
  }
}

type ChangePasswordParams = {
  old_password: string
  new_password: string
}

export const changePassword = async ({
  old_password,
  new_password,
}: ChangePasswordParams): ResponseReturnType => {
  try {
    const userId = getUserId()
    const response = await axios.put(
      `${USER_SERVICE_BASE_URL}/${userId}/password`,
      {
        old_password,
        new_password,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      }
    )
    if (response.status === 200) {
      return {
        status: 'ok',
        data: response.data,
        error: undefined,
      }
    }

    throw new Error(response.data.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response.data.message || 'Something went wrong',
    }
  }
}

type ResetPasswordParams = {
  email: string
  otp_id: string
  otp: string
  password: string
}

export const resetPassword = async ({
  email,
  otp_id,
  otp,
  password,
}: ResetPasswordParams): ResponseReturnType => {
  try {
    const response = await axios.post(
      `${USER_SERVICE_BASE_URL}/reset-password-with-otp`,
      {
        email,
        otp_id,
        otp,
        password,
      }
    )

    if (response.status === 200) {
      return {
        status: 'ok',
        data: response.data,
        error: undefined,
      }
    }

    throw new Error(response.data.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response.data.message || 'Something went wrong',
    }
  }
}

export const getAuthUser = async (): Promise<{
  status: string
  data: User | null
  error: string | undefined
}> => {
  const userId = getUserId()

  if (userId === null)
    return {
      status: 'error',
      data: null,
      error: '',
    }

  const MAX_RETRIES = 10
  let retries = 0
  try {
    while (retries <= MAX_RETRIES) {
      const { status, data } = await fetchUser({ id: userId })
      if (status !== 'ok') {
        retries += 1
        break
      }

      return {
        status: 'ok',
        data,
        error: undefined,
      }
    }

    throw new Error('Could not get user details')
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.meessage,
    }
  }
}

export const validateInviteCode = async (id: string) => {
  try {
    const response = await axios.get(
      `${USER_SERVICE_BASE_URL}/company-access/invites/${id}`
    )

    if (response.status === 200) {
      return {
        status: 'ok',
        data: response.data,
        error: undefined,
      }
    }
    throw new Error(response.data.message)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error?.response.data.message || 'Something went wrong',
    }
  }
}

export const checkEmailExists = async (email: string) => {
  try {
    const response = await axios.get(`${USER_SERVICE_BASE_URL}/email/${email}`)

    if (response.status === 200) {
      return {
        status: 'ok',
        data: response.data.data,
        error: undefined,
      }
    }
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.response.data.message || 'Something went wrong',
    }
  }
}
